import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// =====================================================
// Module-level pure functions - never recreated
// =====================================================

/**
 * Parse date from DD-MM-YYYY format to YYYY-MM-DD
 * Accepts multiple separators: -, /, .
 */
export const parseDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  // Accept multiple separators: -, /, .
  const parts = dateStr.trim().split(/[-/.]/);
  if (parts.length !== 3) return null;

  const day = parts[0].padStart(2, '0');
  const month = parts[1].padStart(2, '0');
  const year = parts[2];

  // Validate parts
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (day < 1 || day > 31) return null;
  if (month < 1 || month > 12) return null;
  if (year.length !== 4) return null;

  return `${year}-${month}-${day}`;
};

/**
 * Validate date format DD-MM-YYYY
 */
export const isValidDateFormat = (dateStr) => {
  const parsed = parseDateDDMMYYYY(dateStr);
  if (!parsed) return false;

  const date = new Date(parsed);
  return !isNaN(date.getTime());
};

/**
 * Parse CSV text into array of row objects
 */
export const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file is empty or contains only headers');
  }

  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const rows = lines.slice(1).map((line, index) => {
    // Handle quoted values with commas
    const values = [];
    let currentValue = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());

    const row = { row_number: index + 2 }; // +2 for header and 1-based indexing
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });
    return row;
  });

  return rows;
};

/**
 * Generate CSV content from headers and rows
 */
export const generateCSV = (headers, rows) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Trigger browser download of content as file
 */
export const downloadFile = (content, filename, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// CSV template headers and example rows
const TEMPLATE_HEADERS = [
  'email',
  'leave_type',
  'start_date',
  'end_date',
  'start_period',
  'end_period',
  'calculated_days',
  'user_note',
  'status'
];

const TEMPLATE_EXAMPLES = [
  [
    'user@example.com',
    'Annual Leave',
    '15-01-2024',
    '17-01-2024',
    'full',
    'full',
    '3',
    'Holiday in Spain',
    'approved'
  ],
  [
    'user@example.com',
    'Sick Leave',
    '10-02-2024',
    '10-02-2024',
    'full',
    'full',
    '1',
    'Flu',
    'approved'
  ]
];

// Valid periods and statuses
const VALID_PERIODS = ['full', 'am', 'pm'];
const VALID_STATUSES = ['approved', 'pending', 'rejected'];

/**
 * Hook for import/export operations
 * Replaces O(n) queries with batch RPC calls
 *
 * @param {string} companyId - Company ID
 * @returns {Object} - Import/export functions and state
 */
export function useImportExport(companyId) {
  const [validationResults, setValidationResults] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Download CSV template
   */
  const downloadTemplate = useCallback(() => {
    const csvContent = generateCSV(TEMPLATE_HEADERS, TEMPLATE_EXAMPLES);
    downloadFile(csvContent, 'leave_import_template.csv');
    return { success: true };
  }, []);

  /**
   * Validate imported CSV data using batch RPC
   * Replaces O(n) duplicate checks with single query
   */
  const validateImport = useCallback(async (csvText) => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    setLoading(true);
    setValidationResults(null);

    try {
      // Parse CSV client-side (file reading must be client-side)
      const rows = parseCSV(csvText);

      // Prepare rows for server validation
      const rowsForValidation = rows.map(row => ({
        row_number: row.row_number,
        email: row.email?.toLowerCase().trim() || '',
        leave_type: row.leave_type || '',
        start_date: parseDateDDMMYYYY(row.start_date),
        end_date: parseDateDDMMYYYY(row.end_date),
        start_period: row.start_period || 'full',
        end_period: row.end_period || 'full',
        calculated_days: row.calculated_days || '',
        user_note: row.user_note || '',
        status: row.status || 'approved',
        // Keep original values for display
        original_start_date: row.start_date,
        original_end_date: row.end_date
      }));

      // Single RPC call for batch validation
      console.log('[useImportExport] Validating with batch RPC...');
      console.time('[useImportExport] Validation RPC');

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'validate_leave_import',
        {
          p_company_id: companyId,
          p_rows: rowsForValidation
        }
      );

      console.timeEnd('[useImportExport] Validation RPC');

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!rpcData.success) {
        throw new Error(rpcData.error);
      }

      // Process validation results client-side using server data
      const users = rpcData.users || {};
      const validLeaveTypes = rpcData.valid_leave_types || [];
      const serverDuplicates = new Set(
        (rpcData.duplicates || []).map(d =>
          `${d.email}|${d.leave_type}|${d.start_date}|${d.end_date}`
        )
      );
      const today = new Date(rpcData.today);

      const results = {
        valid: [],
        invalid: [],
        warnings: [],
        duplicates: []
      };

      for (const row of rowsForValidation) {
        const errors = [];
        const warnings = [];

        // Validate email
        if (!row.email) {
          errors.push('Email is required');
        } else if (!users[row.email]) {
          errors.push(`User with email "${row.email}" not found in company`);
        }

        // Validate leave type
        if (!row.leave_type) {
          errors.push('Leave type is required');
        } else if (!validLeaveTypes.includes(row.leave_type)) {
          errors.push(`Invalid leave type "${row.leave_type}". Valid types: ${validLeaveTypes.join(', ')}`);
        }

        // Validate dates
        if (!row.original_start_date) {
          errors.push('Start date is required');
        } else if (!row.start_date) {
          errors.push('Invalid start date format (use DD-MM-YYYY, DD/MM/YYYY, or DD.MM.YYYY)');
        }

        if (!row.original_end_date) {
          errors.push('End date is required');
        } else if (!row.end_date) {
          errors.push('Invalid end date format (use DD-MM-YYYY, DD/MM/YYYY, or DD.MM.YYYY)');
        }

        if (row.start_date && row.end_date) {
          if (new Date(row.end_date) < new Date(row.start_date)) {
            errors.push('End date must be after start date');
          }
        }

        // Validate periods
        if (row.start_period && !VALID_PERIODS.includes(row.start_period)) {
          errors.push(`Invalid start_period "${row.start_period}". Valid: full, am, pm`);
        }
        if (row.end_period && !VALID_PERIODS.includes(row.end_period)) {
          errors.push(`Invalid end_period "${row.end_period}". Valid: full, am, pm`);
        }

        // Validate status
        if (row.status && !VALID_STATUSES.includes(row.status)) {
          errors.push(`Invalid status "${row.status}". Valid: approved, pending, rejected`);
        }

        // Validate calculated_days
        if (row.calculated_days && isNaN(parseFloat(row.calculated_days))) {
          errors.push('Invalid calculated_days (must be a number)');
        }

        // Check for future dates (warning)
        if (row.start_date && new Date(row.start_date) > today) {
          warnings.push('Start date is in the future');
        }

        // Build validated row
        const validatedRow = {
          rowNumber: row.row_number,
          data: row,
          user: users[row.email] || null,
          errors,
          warnings
        };

        if (errors.length > 0) {
          results.invalid.push(validatedRow);
        } else {
          // Check for duplicates using server-provided set
          const dupeKey = `${row.email}|${row.leave_type}|${row.start_date}|${row.end_date}`;
          if (serverDuplicates.has(dupeKey)) {
            validatedRow.isDuplicate = true;
            results.duplicates.push(validatedRow);
          }

          results.valid.push(validatedRow);
          if (warnings.length > 0) {
            results.warnings.push(validatedRow);
          }
        }
      }

      setValidationResults(results);
      return { success: true, results, rowCount: rows.length };

    } catch (err) {
      console.error('[useImportExport] Validation error:', err);
      setValidationResults(null);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  /**
   * Execute import using batch RPC
   * Replaces O(n) individual inserts with single call
   */
  const executeImport = useCallback(async () => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    if (!validationResults || validationResults.valid.length === 0) {
      return { success: false, error: 'No valid records to import' };
    }

    setLoading(true);
    setImportProgress({ current: 0, total: validationResults.valid.length, status: 'preparing' });

    try {
      // Prepare rows for batch import (exclude duplicates)
      const rowsToImport = validationResults.valid
        .filter(v => !v.isDuplicate)
        .map(v => {
          const row = v.data;

          // Build part_day JSON
          let partDay = null;
          const startPeriod = row.start_period || 'full';
          const endPeriod = row.end_period || 'full';

          if (startPeriod !== 'full' || endPeriod !== 'full') {
            partDay = {};
            if (startPeriod !== 'full') {
              partDay.start = startPeriod.toUpperCase();
            }
            if (endPeriod !== 'full' && row.end_date !== row.start_date) {
              partDay.end = endPeriod.toUpperCase();
            }
          }

          return {
            row_number: v.rowNumber,
            user_id: v.user.id,
            leave_type: row.leave_type,
            start_date: row.start_date,
            end_date: row.end_date,
            part_day: partDay,
            calculated_days: row.calculated_days || null,
            user_note: row.user_note || null,
            status: row.status || 'approved'
          };
        });

      if (rowsToImport.length === 0) {
        setImportProgress(null);
        return {
          success: true,
          inserted: 0,
          skipped: validationResults.duplicates.length,
          message: 'All records were duplicates'
        };
      }

      setImportProgress({ current: 0, total: rowsToImport.length, status: 'importing' });

      // Single RPC call for batch import
      console.log('[useImportExport] Importing with batch RPC...');
      console.time('[useImportExport] Import RPC');

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'import_leave_requests',
        {
          p_company_id: companyId,
          p_rows: rowsToImport
        }
      );

      console.timeEnd('[useImportExport] Import RPC');

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      setImportProgress({
        current: rpcData.inserted + rpcData.skipped,
        total: rowsToImport.length,
        status: 'complete'
      });

      // Clear state after successful import
      setValidationResults(null);
      setImportProgress(null);

      return {
        success: true,
        inserted: rpcData.inserted,
        skipped: rpcData.skipped + validationResults.duplicates.length,
        errors: rpcData.errors || []
      };

    } catch (err) {
      console.error('[useImportExport] Import error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [companyId, validationResults]);

  /**
   * Export all leave data using single-query RPC
   * Replaces 2 sequential queries with 1 JOIN
   */
  const exportData = useCallback(async () => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    setExporting(true);

    try {
      console.log('[useImportExport] Exporting with single RPC...');
      console.time('[useImportExport] Export RPC');

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'export_leave_requests',
        { p_company_id: companyId }
      );

      console.timeEnd('[useImportExport] Export RPC');

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!rpcData.success) {
        throw new Error(rpcData.error);
      }

      const records = rpcData.records || [];

      if (records.length === 0) {
        return { success: false, error: 'No leave records to export' };
      }

      // Build CSV from pre-formatted server data
      const headers = [
        'email',
        'first_name',
        'last_name',
        'leave_type',
        'start_date',
        'end_date',
        'start_period',
        'end_period',
        'calculated_days',
        'user_note',
        'status',
        'created_date'
      ];

      const rows = records.map(record => [
        record.email,
        record.first_name,
        record.last_name,
        record.leave_type_display,
        record.start_date,
        record.end_date,
        record.start_period,
        record.end_period,
        record.calculated_days,
        record.user_note,
        record.status,
        record.created_date
      ]);

      const csvContent = generateCSV(headers, rows);
      const filename = `leave_export_${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(csvContent, filename);

      return { success: true, count: records.length };

    } catch (err) {
      console.error('[useImportExport] Export error:', err);
      return { success: false, error: err.message };
    } finally {
      setExporting(false);
    }
  }, [companyId]);

  /**
   * Reset validation state
   */
  const resetValidation = useCallback(() => {
    setValidationResults(null);
    setImportProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return {
    // Actions
    downloadTemplate,
    validateImport,
    executeImport,
    exportData,
    resetValidation,

    // State
    validationResults,
    importProgress,
    loading,
    exporting,

    // Ref for file input
    fileInputRef
  };
}
